/**
 * WaitlistForm.js — Early Access Signup Form
 *
 * Collects email, salary bracket, and biggest money worry.
 * Sends data to Google Sheets via webhook.
 * Strict validation on all fields.
 */
import React, { useState } from 'react';
import { submitWaitlistEntry, SALARY_BRACKETS, MONEY_WORRIES } from '../utils/waitlistService';

function WaitlistForm({ source = 'landing-waitlist-section', compact = false }) {
    const [email, setEmail] = useState('');
    const [salaryBracket, setSalaryBracket] = useState('');
    const [moneyWorry, setMoneyWorry] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const isValid = email.trim() && emailRegex.test(email.trim()) && salaryBracket && moneyWorry;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Email is required.');
            return;
        }
        if (!emailRegex.test(email.trim())) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!salaryBracket) {
            setError('Please select your salary range.');
            return;
        }
        if (!moneyWorry) {
            setError('Please select your biggest money worry.');
            return;
        }

        setSubmitting(true);

        try {
            const result = await submitWaitlistEntry({ email, salaryBracket, moneyWorry, source });
            if (result.success) {
                setSubmitted(true);
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        }

        setSubmitting(false);
    };

    if (submitted) {
        return (
            <div style={{
                padding: '40px 32px',
                background: 'linear-gradient(160deg, rgba(33,141,141,0.12) 0%, rgba(10,10,10,0.95) 100%)',
                borderRadius: '24px',
                border: '1px solid rgba(33,141,141,0.25)',
                textAlign: 'center',
                maxWidth: '480px',
                margin: '0 auto'
            }}>
                <div style={{ fontSize: '36px', marginBottom: '16px', letterSpacing: '-2px', fontWeight: 700, color: '#32C077' }}>✓</div>
                <h3 style={{ fontSize: '22px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
                    You're on the list!
                </h3>
                <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                    We'll email you when saved profiles, deeper insights, and fun roast-my-spending modes are ready. Thanks for being an early adopter!
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} style={{
            padding: '20px 10px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.08)',
            maxWidth: '480px',
            margin: '0 auto',
            width: '100%'
        }}>
            {/* Email */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{
                    display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.6)',
                    fontWeight: 600, marginBottom: '6px', letterSpacing: '0.3px'
                }}>
                    Email address
                </label>
                <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    style={{
                        width: '100%', padding: '14px 16px', borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.05)',
                        color: '#fff', fontSize: '16px', outline: 'none',
                        fontFamily: 'inherit', transition: 'border-color 0.2s',
                        boxSizing: 'border-box'
                    }}
                    onFocus={e => e.target.style.borderColor = 'rgba(33,141,141,0.5)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
                />
            </div>

            {/* Salary Bracket */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{
                    display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.6)',
                    fontWeight: 600, marginBottom: '6px', letterSpacing: '0.3px'
                }}>
                    Approx. monthly salary
                </label>
                <select
                    value={salaryBracket}
                    onChange={e => setSalaryBracket(e.target.value)}
                    style={{
                        width: '100%', padding: '14px 16px', borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.05)',
                        color: salaryBracket ? '#fff' : 'rgba(255,255,255,0.4)',
                        fontSize: '15px', outline: 'none',
                        fontFamily: 'inherit', appearance: 'none',
                        WebkitAppearance: 'none', cursor: 'pointer',
                        boxSizing: 'border-box',
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23888888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px'
                    }}
                >
                    {SALARY_BRACKETS.map(o => (
                        <option key={o.value} value={o.value} style={{ background: '#1a1a1a', color: '#fff' }}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Money Worry */}
            <div style={{ marginBottom: '20px' }}>
                <label style={{
                    display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.6)',
                    fontWeight: 600, marginBottom: '6px', letterSpacing: '0.3px'
                }}>
                    Biggest money worry
                </label>
                <select
                    value={moneyWorry}
                    onChange={e => setMoneyWorry(e.target.value)}
                    style={{
                        width: '100%', padding: '14px 16px', borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.05)',
                        color: moneyWorry ? '#fff' : 'rgba(255,255,255,0.4)',
                        fontSize: '15px', outline: 'none',
                        fontFamily: 'inherit', appearance: 'none',
                        WebkitAppearance: 'none', cursor: 'pointer',
                        boxSizing: 'border-box',
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23888888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px'
                    }}
                >
                    {MONEY_WORRIES.map(o => (
                        <option key={o.value} value={o.value} style={{ background: '#1a1a1a', color: '#fff' }}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Error */}
            {error && (
                <div style={{
                    marginBottom: '16px', padding: '10px 14px', borderRadius: '10px',
                    background: 'rgba(255,84,89,0.1)', border: '1px solid rgba(255,84,89,0.2)',
                    fontSize: '13px', color: '#FF5459'
                }}>
                    {error}
                </div>
            )}

            {/* Submit */}
            <button
                type="submit"
                disabled={submitting || !isValid}
                style={{
                    width: '100%', padding: '16px',
                    borderRadius: '12px', border: 'none',
                    background: isValid
                        ? 'linear-gradient(135deg, #218D8D, #2AAE7A)'
                        : 'rgba(255,255,255,0.08)',
                    color: isValid ? 'white' : 'rgba(255,255,255,0.3)',
                    fontWeight: 600, fontSize: '16px',
                    cursor: isValid ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                    transition: 'all 0.3s',
                    boxShadow: isValid ? '0 0 32px rgba(33,141,141,0.2)' : 'none'
                }}
                onMouseOver={e => { if (isValid) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 48px rgba(33,141,141,0.35)'; } }}
                onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = isValid ? '0 0 32px rgba(33,141,141,0.2)' : 'none'; }}
            >
                {submitting ? 'Joining...' : 'Join Early Access'}
            </button>

            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)', marginTop: '12px', textAlign: 'center', lineHeight: 1.5 }}>
                We'll only email you about product launches and updates. No spam, ever.
            </p>
        </form>
    );
}

export default WaitlistForm;
